/* eslint-disable react-dom/no-missing-iframe-sandbox */
'use client';

import type { CarouselApi } from '@/components/ui/carousel';
import type { Media } from './types';
import { ActionButton } from '@/components/action-button';
import BottomFixedContainer from '@/components/bottom-fixed-container';
import { Show } from '@/components/conditional-component';
import { MediaModal } from '@/components/media-modal';
import { useUser } from '@/components/providers/user-provider';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { useRazorpayPayment } from '@/hooks/use-razorpay-payment';
import { Env } from '@/lib/Env';
import { ChevronLeft, ChevronRight, Loader2, X } from 'lucide-react';
import { motion } from 'motion/react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { BatchBadge } from '../batch-badge';
import { useBatch } from '../providers/batch-provider';
import { useVideo } from '../providers/video-player-provider';
type MediaItem = {
  type: 'Video' | 'Image';
  src: string;
} | {
  type: string;
  src: string;
};
export function BatchPreviewSection({
  isLoggedIn
}: {
  isLoggedIn: boolean;
}) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [direction, setDirection] = useState(0);
  const [selectedMedia, setSelectedMedia] = useState<Media | null>(null);
  const {
    video,
    setVideo
  } = useVideo();
  const {
    batch,
    isNotValid
  } = useBatch();
  const router = useRouter();
  const {
    user
  } = useUser();
  const photos = batch?.banners.map((photo, index) => ({
    src: `${Env.NEXT_PUBLIC_STATIC_BUCKET_URL}/${photo.resourceId}`,
    alt: 'Our results',
    id: index,
    type: photo.type
  })) || [];
  const {
    processPayment,
    isLoading
  } = useRazorpayPayment({
    batchId: batch?.id,
    onSuccess: () => {
      // Optional: Add any additional success handling specific to this component
    },
    userDetails: {
      name: user?.name,
      // email: user?.email,
      contact: user?.phone
    },
    onError: error => {
      // Optional: Add any additional error handling specific to this component
      console.error('Payment failed', error);
    }
  });

  // Handle current slide tracking
  useEffect(() => {
    if (!api) {
      return;
    }
    api.on('select', () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  // Handle autoplay
  useEffect(() => {
    if (!api || isModalOpen) {
      return;
    }
    const timer = setInterval(() => {
      api.scrollNext();
    }, 5000);
    return () => clearInterval(timer);
  }, [api, isModalOpen]);
  const handleMediaClick = (media: Media) => {
    setSelectedMedia(media);
    setIsModalOpen(true);
  };
  const renderMediaItem = (media: MediaItem) => {
    switch (media.type) {
      case 'Video':
        return <video src={media.src} controls={false} autoPlay muted loop className=" aspect-video w-full rounded-lg object-cover" />;
      case 'Image':
        return <Image height={1000} width={1000} src={media.src} alt="Course Preview" className="max-h-[200px] w-full rounded-lg object-contain md:max-h-[300px] " />;
    }
  };
  const handleBookNow = async () => {
    if (isLoggedIn) {
      await processPayment();
    } else {
      router.replace(`/login?redirect=batchfinder/${batch?.id}`);
    }
  };
  const handleCloseVideo = () => {
    setVideo(null);
  };
  return <>
      <div className="flex w-full flex-col gap-5 overflow-y-scroll border-r border-black/10 bg-white scrollbar-none max-md:max-w-full max-md:px-5 md:h-[calc(90vh-40px)]  md:w-1/2  md:p-5">
        {batch && <>
            <div className="flex flex-col  items-center justify-center bg-white ">
              <div className=" mt-2 w-full items-start  overflow-hidden rounded-md bg-white md:mt-0 md:max-h-[400px] xl:max-h-[500px]">
                {video ? <div className="relative">
                        <button type="button" onClick={handleCloseVideo} className="absolute right-2 top-2 z-10 rounded-full bg-white p-2 shadow-md" aria-label="Close video player">
                          <X className="size-5 text-gray-700" />
                        </button>
                        <div className="aspect-video  rounded-lg">
                          <iframe width="100%" height="100%" title={video.title} src={`https://www.youtube.com/embed/${video.id}`} allow="fullscreen; autoplay; encrypted-media; gyroscope; picture-in-picture" />

                        </div>
                      </div> : <>
                        <div className="group relative">
                          <Carousel setApi={setApi} opts={{
                  align: 'start',
                  loop: true
                }} className="max-h-[400px]">
                            <Show when={photos.length > 1}>
                              <button type="button" onClick={() => api?.scrollPrev()} className="absolute left-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white opacity-0 transition-all group-hover:opacity-100 hover:bg-black/70" aria-label="Previous slide">
                                <ChevronLeft className="size-6" />
                              </button>
                            </Show>

                            <CarouselContent>
                              {photos.map((media, index) => <CarouselItem key={index} className="flex aspect-video max-h-[200px] items-center justify-center md:max-h-[300px]">
                                  <button type="button" className="cursor-pointer p-1" onClick={() => handleMediaClick(media)}>
                                    {renderMediaItem({
                          src: media.src,
                          type: media.type
                        })}
                                  </button>
                                </CarouselItem>)}
                            </CarouselContent>
                            <Show when={photos.length > 1}>
                              <button type="button" onClick={() => api?.scrollNext()} className="absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white opacity-0 transition-all group-hover:opacity-100 hover:bg-black/70" aria-label="Next slide">
                                <ChevronRight className="size-6" />
                              </button>
                            </Show>
                          </Carousel>
                        </div>
                      </>}
                <div className="relative mt-4 flex justify-center gap-1.5">
                  <div className="flex max-w-[200px] gap-1.5 overflow-hidden">
                    {batch.banners.map((_, index) => <motion.button type="button" key={index} onClick={() => api?.scrollTo(index)} animate={{
                  scale: current === index || index === current - 1 || index === current + 1 ? 1 : 0.667
                }} className={`${video ? ' opacity-50' : 'size-[4px]'} origin-center rounded-full ${current === index ? 'size-[6px] bg-orange-500' : 'bg-gray-300'} size-[4px]`} aria-label={`Go to slide ${index + 1}`} />)}
                  </div>
                  {batch.banners.length > 10 && <div className="absolute right-0 h-full w-8 bg-gradient-to-l from-white to-transparent" />}
                </div>
              </div>
            </div>
            <div className="flex w-full flex-col gap-3 max-md:max-w-full ">
              <h1 className="w-full text-2xl font-semibold leading-none tracking-tight text-zinc-900 max-md:max-w-full">
                {batch.title}
              </h1>
              <BatchBadge batchNumber={batch.number} location={batch.branch?.name} mode={batch.mode} isOwn={!!batch.own} isRegular={batch.opportunityType.startsWith('Regular')} />
              <span className="text-lg font-semibold">
                ₹
                {' '}
                {(batch.fees.amount + batch.fees.gst).toLocaleString('en-IN')}
              </span>
              <BottomFixedContainer>
                <ActionButton onClick={handleBookNow} className={`${isNotValid ? 'pointer-events-none grayscale' : ''}`}>
                  {isLoading ? <Loader2 className="size-6 animate-spin text-white" /> : batch?.own ? 'Enrolled' : batch?.fees.isFree ? `Join Now For Free` : `${batch?.fees.componentType === 'Admission' ? 'Book for' : 'Join Now for'} ₹${batch && (batch.fees.amount + batch.fees.gst).toLocaleString('en-IN')}`}
                </ActionButton>
              </BottomFixedContainer>
            </div>
          </>}
      </div>
      <MediaModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} mediaItems={photos} selectedMedia={selectedMedia as Media} setSelectedMedia={setSelectedMedia} direction={direction} setDirection={setDirection} data-sentry-element="MediaModal" data-sentry-source-file="batch-preview-section.tsx" />
      {/* <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogTitle className="sr-only">{batch?.title}</DialogTitle>
        <DialogContent className="max-w-[90vw] p-0 md:max-h-[90vh] ">
          <div className="h-fit overflow-hidden p-4">
            {renderModalContent()}
          </div>
        </DialogContent>
       </Dialog> */}
    </>;
}